// The station's own authenticated api surface (spec/07 §4.1, §5; spec/10 §3.3; F-STN-05, #83).
//
// A paired PC's service authenticates to the api with its **device token** — an EdDSA JWT,
// `aud: ["api","realtime"]`, `kind: station`, `sub: dev_…`, 15 minutes (spec/07 §5) — sent as
// `Authorization: Bearer`. `GET /v1/station/context` is the first route that reads it: the station
// confirms its token is accepted and reads back the identity the api has it bound to, plus the server
// clock.
//
//   GET /v1/station/context   station-authenticated → { stationId, organizationId, venueId, deviceId, mode, serverTime }
//
// It is **not** a heartbeat and writes nothing: a station's presence, health and last-seen live in the
// realtime tier and never touch Postgres (spec/00 "Heartbeats never touch the database", spec/09 §5.3
// `station.hello`/`station.status`, spec/19 §7). This is the token-verification vehicle and a clock
// reference for a service that has just come online — a station verifies its device token, learns who
// the api has it bound to, and reads the authoritative time (a "clock jump" is an alert, spec/10 §6.6).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { DeviceIdSchema, OrganizationIdSchema, StationCommandIdSchema, StationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { jsonResponse, problemResponse } from "../openapi/responses.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { StationModeSchema } from "../realtime/station.js";
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The device token is an ' +
    'EdDSA JWT with `kind: station` and `aud` including `api`; a user session, a member or a player token, ' +
    'an expired token and a revoked device are each refused with `401`.';
// ---------------------------------------------------------------------------------------------
// GET /v1/station/context
// ---------------------------------------------------------------------------------------------
export const StationContextSchema = z
    .object({
    /** The station the device token is bound to (spec/07 §5). */
    stationId: StationIdSchema,
    /** The station's organisation. */
    organizationId: OrganizationIdSchema,
    /** The station's venue. */
    venueId: VenueIdSchema,
    /** The device the token was minted for — the `sub` of the device JWT the api verified. */
    deviceId: DeviceIdSchema,
    /** The station's current mode (spec/10 §6.1): the station reads back the mode the api holds for it. */
    mode: StationModeSchema,
    /** The api's clock, RFC 3339 UTC: a reference for a service that has just come online (spec/10 §6.6). */
    serverTime: IsoDateTimeSchema,
})
    .openapi('StationContext', {
    description: 'The station identity the api has the calling device bound to, and the server clock.',
});
export const getStationContextRoute = createRoute({
    method: 'get',
    path: '/v1/station/context',
    operationId: 'getStationContext',
    tags: ['stations'],
    summary: 'Read the calling station its identity and the server clock',
    description: `${STATION_AUTHENTICATION} Reads back the station, venue and organisation the api has this device ` +
        'bound to, the mode the api holds for the station (spec/10 §6.1), and the server time. Writes ' +
        'nothing: station presence and health are realtime, never HTTP (spec/09 §5.3, spec/19 §7).',
    responses: {
        200: jsonResponse(StationContextSchema, 'The station identity and the server clock.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/snapshot
// ---------------------------------------------------------------------------------------------
export const StationKioskSnapshotSchema = z
    .object({
    /** The organisation the station belongs to — the venue's brand, shown on the kiosk lock screen (spec/12 §3). */
    organizationName: z.string(),
    /** The station's own name, when it has one (the table's `name` is nullable); the kiosk falls back to the number. */
    stationName: z.string().nullable(),
    /** The station's number, unique per venue (spec/04 §3) — "PC 12". */
    stationNumber: z.number().int(),
    /** The station class the number belongs to, in words (spec/13 §12): "Console", "Racing rig". */
    classLabel: z.string(),
    /** The venue's locality, when set — a line of place under the brand on the lock screen. */
    locality: z.string().nullable(),
    /**
     * The logo the lock screen and the session rail show (spec/12 §3): the venue's own logo, else its
     * organisation's (`org.branding`, for dark grounds first), served from public media — the station caches
     * it for offline (ADR-0028). Null: the kiosk shows the organisation's name as a wordmark.
     */
    logoUrl: z.url().nullable(),
})
    .openapi('StationKioskSnapshot', {
    description: 'The kiosk-facing identity the lock screen renders for the calling station: its organisation (brand), ' +
        'name/number, class, locality and logo (the venue’s, else the organisation’s). The hourly rate and attract ' +
        'media ride the signed policy bundle (F-STN-09) and are not here yet.',
});
export const getStationKioskSnapshotRoute = createRoute({
    method: 'get',
    path: '/v1/station/snapshot',
    operationId: 'getStationKioskSnapshot',
    tags: ['stations'],
    summary: 'Read the calling station its kiosk lock-screen identity',
    description: `${STATION_AUTHENTICATION} Returns the station's kiosk-facing identity — organisation (brand), name, number, ` +
        'class and locality — for the service to assemble the kiosk snapshot and push it over the bridge (spec/10 §5.3, ' +
        'spec/12 §2). Reads only; presence and health are realtime (spec/09 §5.3).',
    responses: {
        200: jsonResponse(StationKioskSnapshotSchema, 'The station kiosk identity.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/station/kiosk-token
// ---------------------------------------------------------------------------------------------
export const StationKioskTokenSchema = z
    .object({
    /**
     * The kiosk token (spec/07 §4.3, spec/10 §3.3): an EdDSA JWT, `kind: kiosk`, `aud: api`, bound to the
     * one station, 10 minutes. The service injects it into the WebView2 UI so the kiosk can call
     * `/v1/station/sign-in/*` itself — the device token never reaches the UI.
     */
    kioskToken: z.string().min(1),
    /** When the kiosk token expires; the service refreshes it before then (spec/07 §4.3). */
    expiresAt: IsoDateTimeSchema,
})
    .openapi('StationKioskToken', { description: 'A short-lived kiosk token for the WebView2 UI to sign players in.' });
export const createStationKioskTokenRoute = createRoute({
    method: 'post',
    path: '/v1/station/kiosk-token',
    operationId: 'createStationKioskToken',
    tags: ['stations'],
    summary: 'Mint a kiosk token for the WebView2 UI',
    description: `${STATION_AUTHENTICATION} The service, authenticated with its device token, mints a **kiosk token** — ` +
        '`kind: kiosk`, `aud: api`, bound to this one station, 10 minutes (spec/07 §4.3, spec/10 §3.3) — and injects ' +
        'it into the kiosk so the UI can call the station sign-in routes without ever holding the device token. The ' +
        'sign-in routes accept the kiosk token; every other station route stays device-token only.',
    responses: {
        200: jsonResponse(StationKioskTokenSchema, 'The kiosk token and its expiry.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/policy-bundle (F-STN-09, spec/10 §6.2)
// ---------------------------------------------------------------------------------------------
export const StationPolicyBundleSchema = z
    .object({
    /** The bundle version (spec/10 §6.2): the station applies a bundle only when this differs from the one it holds. */
    version: z.string().min(1).max(64),
    /**
     * The signed bundle: a compact JWS (EdDSA) whose payload is the `PolicyBundle`, signed by the api
     * JWKS (spec/10 §6.2, spec/11 §6). The station verifies it against `GET /v1/station/jwks` and applies
     * it transactionally. This is the same signed form `station.policy.update` delivers over realtime.
     */
    signedBundle: z.string().min(1),
})
    .openapi('StationPolicyBundle', {
    description: 'The station\'s current signed policy bundle and its version (spec/10 §6.2, F-STN-09).',
});
export const getStationPolicyBundleRoute = createRoute({
    method: 'get',
    path: '/v1/station/policy-bundle',
    operationId: 'getStationPolicyBundle',
    tags: ['stations'],
    summary: 'Read the calling station its current signed policy bundle',
    description: `${STATION_AUTHENTICATION} Returns the station's current **signed policy bundle** — the resolved locker ` +
        'and cleanup policy, offline policy, warning thresholds, attract media and library version, as a compact ' +
        'EdDSA JWS the station verifies against the api JWKS and applies transactionally (spec/10 §6.2, spec/11 §6). ' +
        'The station pulls this on boot and reconnect, and whenever `station.state` names a newer `policyVersion`.',
    responses: {
        200: jsonResponse(StationPolicyBundleSchema, 'The current signed policy bundle and its version.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/library (F-CAT-02, F-CAT-05, spec/10 §7.1)
// ---------------------------------------------------------------------------------------------
export const StationLibrarySchema = z
    .object({
    /** The library version: the station replaces its cache only when this differs from the one it holds. */
    version: z.string().min(1).max(64),
    /**
     * The signed library: a compact JWS (EdDSA, the api JWKS) whose payload is the `LibraryBundle` plus
     * `iss` and `aud: station-library`. The station verifies it against `GET /v1/station/jwks`.
     */
    signedLibrary: z.string().min(1),
})
    .openapi('StationLibrary', {
    description: 'The station\'s current signed library and its version (spec/10 §7.1, F-CAT-02).',
});
export const getStationLibraryRoute = createRoute({
    method: 'get',
    path: '/v1/station/library',
    operationId: 'getStationLibrary',
    tags: ['stations'],
    summary: 'Read the calling station its signed game library',
    description: `${STATION_AUTHENTICATION} Returns the **signed library bundle** — the venue library's visible entries ` +
        'for this station\'s class, each with its name, cover and launch recipes — as a compact EdDSA JWS the ' +
        'station verifies against the api JWKS (spec/10 §7.1, §5.2). The station pulls it on boot and on every ' +
        'connect, hands the recipes to the agent that launches titles, and the catalogue to the kiosk.',
    responses: {
        200: jsonResponse(StationLibrarySchema, 'The current signed library and its version.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// GET /v1/station/jwks (F-STN-09, spec/07 §5, spec/10 §6.2)
// ---------------------------------------------------------------------------------------------
//
// The trust anchor for the signed policy bundle: the api's **public** JWKS, the keys the bundle (and the
// device, realtime and kiosk tokens) are signed with. A paired station fetches this over its own
// authenticated channel and caches it, then verifies a bundle from any delivery path (the fetch above,
// or `station.policy.update` inline / by CDN url) against it — the signature, never the transport, is
// what the station trusts. better-auth's own public `GET /v1/auth/jwks` stays switched off (#458); this
// is the station's device-authenticated read of the same keys.
export const StationJwkSchema = z
    .object({
    kty: z.string().min(1),
    kid: z.string().min(1).optional(),
    crv: z.string().min(1).optional(),
    /** The Ed25519 public key, base64url (JWK `x`). */
    x: z.string().min(1).optional(),
    alg: z.string().min(1).optional(),
})
    .meta({ id: 'StationJwk' });
export const StationJwksSchema = z
    .object({
    keys: z.array(StationJwkSchema),
})
    .openapi('StationJwks', {
    description: 'The api\'s public JWKS: the Ed25519 keys the signed policy bundle and the station tokens are verified with (spec/07 §5, spec/10 §6.2).',
});
export const getStationJwksRoute = createRoute({
    method: 'get',
    path: '/v1/station/jwks',
    operationId: 'getStationJwks',
    tags: ['stations'],
    summary: 'Read the api public JWKS the station verifies signed bundles against',
    description: `${STATION_AUTHENTICATION} Returns the api's **public** JWKS — the Ed25519 keys the signed policy bundle ` +
        '(F-STN-09) and the station tokens are signed with. The station caches these and verifies a bundle from any ' +
        'delivery path against them; the signature, not the transport, is the trust (spec/07 §5, spec/10 §6.2).',
    responses: {
        200: jsonResponse(StationJwksSchema, 'The api public JWKS.'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
// ---------------------------------------------------------------------------------------------
// POST /v1/station/messages/{commandId}/read
// ---------------------------------------------------------------------------------------------
export const STATION_MESSAGE_READ_VIA = ['kiosk', 'overlay'];
export const ReadStationMessageSchema = z
    .object({
    /** Where the player acknowledged it: **Got it** on the kiosk, or `Ctrl+Shift+Space` on the in-game overlay. */
    via: z.enum(STATION_MESSAGE_READ_VIA),
    /** When the player acknowledged it, by the station's clock (RFC 3339 UTC). */
    at: IsoDateTimeSchema,
})
    .openapi('ReadStationMessage', {
    description: 'The player acknowledged a staff message that needed it (spec/09 §6.2, F-STN-20).',
});
export const StationMessageReadSchema = z
    .object({
    /** The `session.message` command that was read. */
    commandId: StationCommandIdSchema,
    /** When the player acknowledged it (the first report wins; a repeat changes nothing). */
    readAt: IsoDateTimeSchema,
    via: z.enum(STATION_MESSAGE_READ_VIA),
})
    .openapi('StationMessageRead', {
    description: 'The read receipt the api holds for a staff message (spec/09 §6.2, F-STN-20).',
});
export const readStationMessageRoute = createRoute({
    method: 'post',
    path: '/v1/station/messages/{commandId}/read',
    operationId: 'readStationMessage',
    tags: ['stations'],
    summary: 'Report that the player acknowledged a staff message (F-STN-20)',
    description: `${STATION_AUTHENTICATION} A \`session.message\` sent with \`needsAck\` stays on the kiosk and the in-game ` +
        'overlay until the player acknowledges it — **Got it** on the kiosk, or `Ctrl+Shift+Space` over a game. The ' +
        'station reports that here, naming the command; the api records the read on the command (its `result` gains ' +
        '`readAt` and `readVia`) and sends the member who sent it `command.updated`, so the console shows it read ' +
        '(spec/09 §6.2). Idempotent: a repeat answers the first read. `404` when the command is not a message to this station.',
    request: {
        params: z.object({ commandId: StationCommandIdSchema }),
        body: { content: { 'application/json': { schema: ReadStationMessageSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(StationMessageReadSchema, 'The read, as recorded.'),
        400: problemResponse('The body is not a read report.', 'validation-failed'),
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such message to this station.', 'not-found'),
        500: problemResponse('Something went wrong.', 'internal-error'),
    },
});
export const STATION_ROUTES = [
    getStationContextRoute,
    getStationKioskSnapshotRoute,
    createStationKioskTokenRoute,
    getStationPolicyBundleRoute,
    getStationLibraryRoute,
    getStationJwksRoute,
    readStationMessageRoute,
];
//# sourceMappingURL=station.js.map